import axios from "axios";

export const SELECTED_API = "selected_api";
const hostname = window.location.hostname;
// "Production" is only the canonical prod hostname. Anywhere else (localhost,
// staging, ngrok tunnels, preview deploys) defaults to the staging API.
const isProduction = hostname === "football.smrtstats.com";
// Hosts where /lexicon is proxied by nginx to the lexicon service. Other
// origins (localhost, ngrok) need to hit the absolute URL directly.
const lexiconProxied = hostname === "football.smrtstats.com" || hostname === "football-stage.smrtstats.com";

export const VIDEO_SERVICE_URL = "https://video-platform.smrtstats.com";

export const normalizeApiBaseUrl = (value = "") =>
  String(value || "")
    .trim()
    .replace(/\/+$/, "")
    .replace(/(?:\/api\/v\d+)+$/i, "");

export const buildVersionedApiUrl = (baseUrl, version) =>
  `${normalizeApiBaseUrl(baseUrl)}/api/${version}`;

let target_api_url = isProduction
  ? "https://api-v1.smrtstats.com"
  : "https://api-v1-stage.smrtstats.com";

if (localStorage.getItem("target_api_url")) {
  target_api_url = localStorage.getItem("target_api_url");
}

target_api_url = normalizeApiBaseUrl(target_api_url);

console.log("target_api_url", target_api_url);

export const API_URL_V1 = buildVersionedApiUrl(
  process.env.REACT_APP_API_URL_V1 || target_api_url,
  "v1"
);
export const API_URL_V2 = buildVersionedApiUrl(
  process.env.REACT_APP_API_URL_V2 || target_api_url,
  "v2"
);
export const API_URL_V5 = buildVersionedApiUrl(
  process.env.REACT_APP_API_URL_V5 || target_api_url,
  "v5"
);
export const API_URL_V6 = buildVersionedApiUrl(
  process.env.REACT_APP_API_URL_V6 || target_api_url,
  "v6"
);

export const $apiV2 = axios.create({
  baseURL: API_URL_V2,
});

export const $apiV5 = axios.create({
  baseURL: API_URL_V5,
});

export const $apiV6 = axios.create({
  baseURL: API_URL_V6,
});

export const $apiV1 = axios.create({
  baseURL: API_URL_V1,
});

// E2E instrumentation: expose the resolved API base in development builds only
// so Playwright cleanup hooks can call the same API the app uses.
if (process.env.NODE_ENV === "development" && typeof window !== "undefined") {
  window.__apiBaseV1__ = API_URL_V1;
}

export const LEXICON_API_URL = lexiconProxied ? "/lexicon" : "https://lexicon.smrtstats.com/api";
export const $api_lang = axios.create({ baseURL: LEXICON_API_URL });

$apiV1.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  // eslint-disable-next-line no-param-reassign
  config.headers.Authorization = token ? `Bearer ${token}` : "";
  return config;
});

$apiV2.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  // eslint-disable-next-line no-param-reassign
  config.headers.Authorization = token ? `Bearer ${token}` : "";

  // Admin "view-as" (C1): a platform admin browsing the player PWA has no
  // player mapping of their own, so they pick a player to inspect and we
  // forward that id as `as_player` on the PWA self endpoints (/player/*).
  // The backend PlayerDataFilter reads it only for platform_admin callers,
  // so it is harmless/ignored for real player accounts. Set in /player/view-as.
  const viewAsPlayerId = localStorage.getItem("view_as_player_id");
  if (
    viewAsPlayerId &&
    typeof config.url === "string" &&
    config.url.startsWith("/player/")
  ) {
    // eslint-disable-next-line no-param-reassign
    config.params = { ...(config.params || {}), as_player: viewAsPlayerId };
  }
  return config;
});

$apiV5.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  // eslint-disable-next-line no-param-reassign
  config.headers.Authorization = token ? `Bearer ${token}` : "";
  return config;
});

$apiV6.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  // eslint-disable-next-line no-param-reassign
  config.headers.Authorization = token ? `Bearer ${token}` : "";
  return config;
});

// 402 interceptor — branches on the response `code` so the user lands on
// the right remediation page. Mirrors the backend CheckSubscription
// middleware contract (PRD §10.3, §11.10).
//
// Pages that the user is *already* on don't trigger a redirect (so a
// 402 fired by an in-page poll doesn't loop them through /subscription/*
// while they're already there).
// Where a 402 `code` sends the user. Exported for tests. `null` = stay put
// (the page renders the notice itself).
export const resolve402Target = (code, pathname = "") => {
  if (/^\/shared\/playlist\/[^/]+\/?$/i.test(pathname)) return null;
  switch (code) {
    case "EMAIL_NOT_VERIFIED":
      return "/player/verify-email-pending";
    case "CARD_NOT_VERIFIED":
      return "/player/setup-card";
    case "ORGANIZATION_SUSPENDED":
      // The organization (agency / club) was switched off commercially.
      // Its admin sees the notice inside the admin screens; a player is
      // taken to the access-ended page.
      if (/^\/(team-admin|club-admin)(\/|$)/.test(pathname)) return null;
      return "/player/access-suspended";
    case "SUBSCRIPTION_REQUIRED":
    default:
      return "/subscription/expired";
  }
};

export const ACCESS_SUSPENDED_STORAGE_KEY = "smrt_access_suspended";

const handleSubscriptionRequired = async (error) => {
  const status = error?.response?.status;
  if (status !== 402) {
    return Promise.reject(error);
  }

  // Blob payloads (responseType:'blob' downloads) hide the JSON body. Decode
  // once so we can read `code` and the inline-caller can re-read it too.
  // Replace the Blob with the parsed object on the error so downstream catch
  // handlers don't have to do this dance themselves.
  let data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      data = JSON.parse(text);
      error.response.data = data;
    } catch {
      // Couldn't parse — fall back to the legacy expired-page redirect.
    }
  }
  const code = data?.code;

  // Reports Add-on PRD §6 — the entitlement-gate codes (trial-exhausted,
  // addon-required, payment-required for the off-session card charge) are
  // handled inline by the report download button; the interceptor must NOT
  // redirect or the user gets bounced away from the page that's showing
  // the upgrade sheet. Bubble the error so the caller branches on `code`.
  if (
    code === "REPORT_TRIAL_EXHAUSTED" ||
    code === "REPORT_ADDON_REQUIRED" ||
    code === "ADDON_PAYMENT_REQUIRED" ||
    code === "ADDON_NOT_SELF_SERVE" ||
    code === "BUNDLE_CHECKOUT_REQUIRED"
  ) {
    return Promise.reject(error);
  }

  if (code === "ORGANIZATION_SUSPENDED") {
    // Keep the organization details for the access-ended page (the page
    // itself has no token-free endpoint to re-fetch them from).
    try {
      sessionStorage.setItem(
        ACCESS_SUSPENDED_STORAGE_KEY,
        JSON.stringify({
          organization: data?.organization ?? null,
          reason: data?.reason ?? null,
          billing_track: data?.billing_track ?? null,
        })
      );
    } catch {
      // storage unavailable — the page falls back to generic copy
    }
  }

  const target = resolve402Target(code, window.location.pathname);
  if (target && window.location.pathname !== target) {
    window.location.href = target;
  }
  return Promise.reject(error);
};

$apiV1.interceptors.response.use((r) => r, handleSubscriptionRequired);
$apiV2.interceptors.response.use((r) => r, handleSubscriptionRequired);
$apiV6.interceptors.response.use((r) => r, handleSubscriptionRequired);
